import { createApi } from "@reduxjs/toolkit/query/react";
import axiosNewBaseQuery from "../../util/axiosNewBaseQuery";
import { roomWeekApiSlice } from "../roomWeek/roomWeekApiSlice";
import {
  AccountDataExport,
  AdministeredOrganization,
  MemberOrganization,
  OrganizationHallTimes,
  OrganizationPlan,
  SeriesCreateResponse,
  SeriesOptions,
  SeriesPreviewOccurrence,
  SeriesRequestBody,
  ContactInviteResult,
  ContactMemberList,
  MembershipClaim,
  AccountProfile,
  BookingSummary,
  ConfirmEmailChangePayload,
  ConfirmOtpPayload,
  ConfirmOtpResponse,
  DeleteAccountPayload,
  PasskeyOptionsResponse,
  PasskeyVerifyPayload,
  PatchMePayload,
  RequestEmailChangePayload,
  RequestOtpPayload,
} from "./types";

/** After a hall-time release or restore: the room weeks show the change. */
const evictRoomWeeks = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  dispatch(roomWeekApiSlice.util.invalidateTags(["RoomWeek"]));
};

/**
 * H-3a end-user account system — email + OTP login against the new backend.
 * The request endpoint is enumeration-safe: it always answers 204, whether or
 * not an account exists for the email.
 */
export const accountApiSlice = createApi({
  reducerPath: "accountApiSlice",
  baseQuery: axiosNewBaseQuery(),
  tagTypes: [
    "AccountProfile",
    "AccountBookings",
    "MyMemberships",
    "AdministeredOrganizations",
    "OrganizationMembers",
    "MemberOrganizations",
    "OrganizationBookings",
    "OrganizationHallTimes",
  ],
  endpoints: (builder) => ({
    requestOtp: builder.mutation<null, RequestOtpPayload>({
      query: (data) => ({
        url: "/auth/otp/request",
        method: "POST",
        data,
      }),
      // 204 has no body; normalize to null so RTK Query doesn't warn on
      // an undefined payload.
      transformResponse: () => null,
    }),
    confirmOtp: builder.mutation<ConfirmOtpResponse, ConfirmOtpPayload>({
      query: (data) => ({
        url: "/auth/otp/confirm",
        method: "POST",
        data,
      }),
    }),
    logout: builder.mutation<null, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      transformResponse: () => null,
    }),
    getMe: builder.query<AccountProfile, void>({
      query: () => ({ url: "/me" }),
      providesTags: ["AccountProfile"],
    }),
    // ---- F2: profile editing, email change, bookings, GDPR delete ----
    patchMe: builder.mutation<AccountProfile, PatchMePayload>({
      query: (data) => ({
        url: "/me",
        method: "PATCH",
        data,
      }),
    }),
    requestEmailChange: builder.mutation<null, RequestEmailChangePayload>({
      query: (data) => ({
        url: "/me/email-change",
        method: "POST",
        data,
      }),
      // 204 has no body; normalize to null (same as requestOtp).
      transformResponse: () => null,
    }),
    confirmEmailChange: builder.mutation<
      AccountProfile,
      ConfirmEmailChangePayload
    >({
      query: (data) => ({
        url: "/me/email-change/confirm",
        method: "POST",
        data,
      }),
    }),
    getMyBookings: builder.query<BookingSummary[], void>({
      query: () => ({ url: "/me/bookings" }),
      providesTags: ["AccountBookings"],
    }),
    // Self-service cancellation of the account's own covered booking (#1495).
    // Invalidates the list so the summary reflects the cancelled state; the
    // caller refetches the old-backend order detail itself.
    cancelMyBooking: builder.mutation<
      { canceled: boolean; mailSent: boolean },
      // W1-6: `expectedFee` is the fee the dialog showed; the server refuses
      // a fee that changed since (409 fee_changed) instead of charging it unseen.
      { orderId: string; expectedFee?: number }
    >({
      query: ({ orderId, expectedFee }) => ({
        url: `/me/bookings/${orderId}/cancel`,
        method: "POST",
        ...(expectedFee === undefined ? {} : { body: { expectedFee } }),
      }),
      invalidatesTags: ["AccountBookings"],
    }),
    // H-3a passkeys (#1901). The ceremony is two round trips each way: the
    // server issues options + a challenge id, the browser talks to the
    // authenticator, the server verifies. Not cached — every ceremony needs a
    // fresh challenge.
    passkeyRegisterOptions: builder.mutation<PasskeyOptionsResponse, void>({
      query: () => ({ url: "/auth/passkey/register/options", method: "POST" }),
    }),
    passkeyRegisterVerify: builder.mutation<
      { registered: boolean },
      PasskeyVerifyPayload
    >({
      query: (data) => ({
        url: "/auth/passkey/register/verify",
        method: "POST",
        data,
      }),
    }),
    passkeyLoginOptions: builder.mutation<PasskeyOptionsResponse, void>({
      query: () => ({ url: "/auth/passkey/login/options", method: "POST" }),
    }),
    passkeyLoginVerify: builder.mutation<ConfirmOtpResponse, PasskeyVerifyPayload>({
      query: (data) => ({
        url: "/auth/passkey/login/verify",
        method: "POST",
        data,
      }),
    }),

    // The organizations this account belongs to. Read-only: membership arrives
    // by invitation, and nothing in the app asks to join (ADR-0021 — the
    // self-claim picker was the tenant's customer list).
    //
    // NOTHING invalidates `MyMemberships`: invites are redeemed server-side at
    // login and by an admin's approval, neither of which touches this cache,
    // and login does not reset this slice (only logout, GDPR delete and the
    // session-init 401 do). The tag alone therefore keeps a stale answer —
    // e.g. an empty list from before an approval — so `MembershipsSection`
    // refetches on every mount (`refetchOnMountOrArgChange`). A new consumer
    // must do the same, or invalidate this tag where it changes membership.
    getMyMemberships: builder.query<MembershipClaim[], void>({
      query: () => ({ url: "/me/memberships" }),
      providesTags: ["MyMemberships"],
    }),

    // ── Delegated member administration (#2163) ────────────────────────
    // The account's SECOND scope: these answer about OTHER people, bounded
    // to one organization the SERVER resolves from the caller's `owner`
    // membership. The client never sends "I am a contact" — it asks, and an
    // organization it does not administer comes back 404 (never 403, which
    // would confirm the id exists).
    //
    // Keyed on `organizationId`, so unlike the argument-less queries above
    // two administered organizations cannot share a cache entry. They live
    // in this slice on purpose: `AccountPage` already wipes it with
    // `resetApiState()` on logout, so a roster cannot survive a logout on a
    // shared device.
    getAdministeredOrganizations: builder.query<AdministeredOrganization[], void>({
      query: () => ({ url: "/me/organizations" }),
      providesTags: ["AdministeredOrganizations"],
    }),
    getOrganizationMembers: builder.query<ContactMemberList, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `/me/organizations/${organizationId}/members`,
      }),
      providesTags: ["OrganizationMembers"],
    }),
    // No optimistic update: the outcome is decided per address server-side
    // (created / already a member / already invited / invalid), so an
    // optimistic row would claim "invited" for an address the server
    // rejected. Submit, then render what came back.
    addOrganizationMembers: builder.mutation<
      ContactInviteResult[],
      { organizationId: string; emails: string[] }
    >({
      query: ({ organizationId, emails }) => ({
        url: `/me/organizations/${organizationId}/members`,
        method: "POST",
        data: { emails },
      }),
      invalidatesTags: ["OrganizationMembers"],
    }),
    removeOrganizationMember: builder.mutation<
      null,
      { organizationId: string; email: string }
    >({
      query: ({ organizationId, email }) => ({
        url: `/me/organizations/${organizationId}/members/${encodeURIComponent(email)}`,
        method: "DELETE",
      }),
      transformResponse: () => null,
      invalidatesTags: ["OrganizationMembers"],
    }),

    // ── The organization's booking plan (#3037, M6) ────────────────────
    // Every approved member reads it; only owners cancel. Same server-side
    // stance as the roster above: 404 for anything the session does not
    // prove, never 403.
    getMemberOrganizations: builder.query<MemberOrganization[], void>({
      query: () => ({ url: "/me/member-organizations" }),
      providesTags: ["MemberOrganizations"],
    }),
    getOrganizationBookings: builder.query<OrganizationPlan, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `/me/organizations/${organizationId}/bookings`,
      }),
      providesTags: ["OrganizationBookings"],
    }),
    // The cancelled booking may be the caller's own, so the personal
    // bookings list is refreshed with the plan.
    cancelOrganizationBooking: builder.mutation<
      { canceled: boolean; mailSent: boolean },
      { organizationId: string; orderId: string }
    >({
      query: ({ organizationId, orderId }) => ({
        url: `/me/organizations/${organizationId}/bookings/${orderId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["OrganizationBookings", "AccountBookings"],
    }),

    // ── Series booking for the organisation (series booking, step 11) ──
    // Owners only; the series module decides dates, conflicts, season and
    // coverage. A created series lands in the plan and in the owner's own
    // bookings, so both are refreshed.
    getOrganizationSeriesOptions: builder.query<SeriesOptions, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `/me/organizations/${organizationId}/series/options`,
      }),
    }),
    previewOrganizationSeries: builder.mutation<
      { occurrences: SeriesPreviewOccurrence[] },
      { organizationId: string; body: SeriesRequestBody }
    >({
      query: ({ organizationId, body }) => ({
        url: `/me/organizations/${organizationId}/series/preview`,
        method: "POST",
        data: body,
      }),
    }),
    createOrganizationSeries: builder.mutation<
      SeriesCreateResponse,
      { organizationId: string; body: SeriesRequestBody }
    >({
      query: ({ organizationId, body }) => ({
        url: `/me/organizations/${organizationId}/series`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["OrganizationBookings", "AccountBookings"],
    }),

    // ── "Our hall times" (W2-1) ────────────────────────────────────────
    // Every member reads the organisation's allocation windows date by date;
    // owners release a date (until it starts) and take it back. The admin
    // service writes the release; no mail is sent. A release changes who may
    // book that time, so the room weeks are evicted too (`roomWeekApiSlice`,
    // in onQueryStarted, since it is another slice).
    getOrganizationHallTimes: builder.query<OrganizationHallTimes, { organizationId: string; weeks?: number }>({
      query: ({ organizationId, weeks }) => ({
        url: `/me/organizations/${organizationId}/windows`,
        ...(weeks ? { params: { weeks } } : {}),
      }),
      providesTags: ["OrganizationHallTimes"],
    }),
    releaseOrganizationHallTime: builder.mutation<
      unknown,
      { organizationId: string; windowId: string; date: string }
    >({
      query: ({ organizationId, windowId, date }) => ({
        url: `/me/organizations/${organizationId}/windows/${windowId}/releases`,
        method: "POST",
        data: { date },
      }),
      invalidatesTags: ["OrganizationHallTimes"],
      onQueryStarted: evictRoomWeeks,
    }),
    restoreOrganizationHallTime: builder.mutation<
      unknown,
      { organizationId: string; windowId: string; date: string }
    >({
      query: ({ organizationId, windowId, date }) => ({
        url: `/me/organizations/${organizationId}/windows/${windowId}/releases/${date}`,
        method: "DELETE",
      }),
      invalidatesTags: ["OrganizationHallTimes"],
      onQueryStarted: evictRoomWeeks,
    }),

    // DSGVO self-service data access (#1679). A lazy query rather than a
    // plain link: the request needs the session bearer, which an <a href>
    // cannot carry, so the JSON comes back here and the download is built
    // client-side.
    getMyDataExport: builder.query<AccountDataExport, void>({
      query: () => ({ url: "/me/export" }),
    }),
    requestAccountDelete: builder.mutation<null, void>({
      query: () => ({
        url: "/me/delete-request",
        method: "POST",
      }),
      transformResponse: () => null,
    }),
    deleteAccount: builder.mutation<null, DeleteAccountPayload>({
      query: (data) => ({
        url: "/me",
        method: "DELETE",
        data,
      }),
      transformResponse: () => null,
    }),
  }),
});

export const {
  useRequestOtpMutation,
  useConfirmOtpMutation,
  useLogoutMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  usePatchMeMutation,
  useRequestEmailChangeMutation,
  useConfirmEmailChangeMutation,
  useGetMyBookingsQuery,
  useCancelMyBookingMutation,
  useLazyGetMyDataExportQuery,
  useGetMyMembershipsQuery,
  useGetAdministeredOrganizationsQuery,
  useGetOrganizationMembersQuery,
  useAddOrganizationMembersMutation,
  useRemoveOrganizationMemberMutation,
  useGetMemberOrganizationsQuery,
  useGetOrganizationBookingsQuery,
  useCancelOrganizationBookingMutation,
  useGetOrganizationSeriesOptionsQuery,
  usePreviewOrganizationSeriesMutation,
  useCreateOrganizationSeriesMutation,
  useGetOrganizationHallTimesQuery,
  useReleaseOrganizationHallTimeMutation,
  useRestoreOrganizationHallTimeMutation,
  usePasskeyRegisterOptionsMutation,
  usePasskeyRegisterVerifyMutation,
  usePasskeyLoginOptionsMutation,
  usePasskeyLoginVerifyMutation,
  useRequestAccountDeleteMutation,
  useDeleteAccountMutation,
} = accountApiSlice;
